import React, { useState } from 'react';
import Header from '@/components/layout/Header';
import { 
  Video, 
  Plus, 
  Calendar,
  Clock,
  Users,
  Ticket,
  MessageSquare,
  Share2,
  Play,
  PauseCircle,
  Settings,
  RadioTower
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { format, addDays, addHours } from 'date-fns';

// Mock scheduled streams
const scheduledStreams = [
  {
    id: 1,
    title: "New Single Release Party",
    scheduledFor: addDays(new Date(), 2),
    duration: "1 hour",
    platform: "YouTube",
    status: "scheduled",
    attendees: 0,
    thumbnailUrl: "",
    description: "Join me for the premiere of my new single 'Midnight Dreams'. I'll be playing the track, sharing the story behind it, and answering your questions live!"
  },
  {
    id: 2,
    title: "Studio Session: Behind the Scenes",
    scheduledFor: addDays(new Date(), 5),
    duration: "2 hours",
    platform: "Instagram",
    status: "scheduled",
    attendees: 0,
    thumbnailUrl: "",
    description: "Going live from the studio to show you how I'm producing my upcoming EP. Come hang out and see the creative process in action!"
  }
];

// Mock past streams
const pastStreams = [
  {
    id: 3,
    title: "Acoustic Set from Home",
    streamedOn: addDays(new Date(), -7),
    duration: "45 minutes",
    platform: "Twitch",
    views: 342,
    likes: 87,
    comments: 64,
    recordingUrl: ""
  }
];

const StreamCard = ({ stream, isPast = false }: { stream: any, isPast?: boolean }) => {
  const { toast } = useToast();
  
  const handleManage = () => {
    toast({
      title: isPast ? "Opening stream analytics" : "Managing stream",
      description: isPast ? `Viewing analytics for ${stream.title}` : `Opening management panel for ${stream.title}`
    });
  };
  
  const handleGoLive = () => {
    toast({
      title: "Going live",
      description: "Preparing your live stream setup..."
    });
  };
  
  return (
    <Card className="neo-card overflow-hidden hover-lift">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg">{stream.title}</CardTitle>
          {!isPast && (
            <Badge variant="outline" className="flex items-center gap-1">
              {stream.platform}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pb-2">
        {!isPast ? (
          <>
            <p className="text-sm text-muted-foreground">{stream.description}</p>
            
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="font-medium">{format(stream.scheduledFor, "EEEE, MMMM d, yyyy")}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{format(stream.scheduledFor, "h:mm a")} • {stream.duration}</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="font-medium">{format(stream.streamedOn, "MMMM d, yyyy")}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{stream.duration}</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-sm font-medium">{stream.views}</div>
                <div className="text-xs text-muted-foreground">Views</div>
              </div>
              <div>
                <div className="text-sm font-medium">{stream.likes}</div>
                <div className="text-xs text-muted-foreground">Likes</div>
              </div>
              <div>
                <div className="text-sm font-medium">{stream.comments}</div>
                <div className="text-xs text-muted-foreground">Comments</div>
              </div>
            </div>
          </>
        )}
      </CardContent>
      <CardFooter className="pt-2 flex justify-between">
        {!isPast ? (
          <>
            <Button variant="outline" size="sm" onClick={handleManage}>Manage</Button>
            <Button size="sm" className="gap-1" onClick={handleGoLive}>
              <RadioTower className="h-4 w-4" />
              <span>Go Live</span>
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" className="gap-1" asChild>
              <a href={stream.recordingUrl || "#"}>
                <Play className="h-4 w-4" />
                <span>Watch</span>
              </a>
            </Button>
            <Button size="sm" onClick={handleManage}>Analytics</Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};

const LiveStreamingSystem = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const { toast } = useToast();
  
  const handleCreate = () => {
    toast({
      title: "Create new stream",
      description: "Opening stream creation wizard"
    });
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <Tabs 
          defaultValue="upcoming" 
          className="animate-slide-up w-full"
          onValueChange={setActiveTab}
        >
          <TabsList className="grid grid-cols-3 w-full sm:w-fit">
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past">Past Streams</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>
          
          <div className="mt-6">
            <TabsContent value="upcoming">
              {scheduledStreams.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {scheduledStreams.map(stream => (
                    <StreamCard key={stream.id} stream={stream} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Video className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <h3 className="text-lg font-medium mb-2">No upcoming streams</h3>
                  <p className="text-muted-foreground mb-6">
                    Schedule your first live stream to connect with your fans
                  </p>
                  <Button className="gap-2" onClick={handleCreate}>
                    <Plus className="h-4 w-4" />
                    <span>Schedule Stream</span>
                  </Button>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="past">
              {pastStreams.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {pastStreams.map(stream => (
                    <StreamCard key={stream.id} stream={stream} isPast={true} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Video className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <h3 className="text-lg font-medium mb-2">No past streams</h3>
                  <p className="text-muted-foreground">
                    Your stream history will appear here
                  </p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="analytics">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="neo-card">
                  <CardHeader>
                    <CardTitle className="text-base">Audience Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center py-10">
                      <Users className="h-16 w-16 mx-auto mb-4 opacity-20" />
                      <h3 className="text-base font-medium mb-2">No audience data yet</h3>
                      <p className="text-sm text-muted-foreground">
                        Complete your first live stream to see audience analytics
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="neo-card">
                  <CardHeader>
                    <CardTitle className="text-base">Engagement Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center py-10">
                      <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-20" />
                      <h3 className="text-base font-medium mb-2">No engagement data yet</h3>
                      <p className="text-sm text-muted-foreground">
                        Complete your first live stream to see engagement metrics
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </div>
        </Tabs>
        
        <Button className="gap-2 mt-0 shrink-0" onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          <span>Create Stream</span>
        </Button>
      </div>
      
      <Card className="neo-card mt-8">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />
            Stream Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-border rounded-md p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Video className="h-4 w-4 text-muted-foreground" />
                Platform Connections
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">YouTube</span>
                  <Badge variant="outline" className="text-muted-foreground">Not Connected</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Twitch</span>
                  <Badge variant="outline" className="text-muted-foreground">Not Connected</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Instagram</span>
                  <Badge variant="outline" className="text-muted-foreground">Not Connected</Badge>
                </div>
              </div>
              <Button size="sm" className="w-full mt-4">Connect Platforms</Button>
            </div>
            
            <div className="border border-border rounded-md p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                Audience Engagement
              </h3>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs">Comment Moderation</span>
                    <Badge variant="outline" className="text-xs">Enabled</Badge>
                  </div>
                  <Progress value={100} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs">Polls & Q&A</span>
                    <Badge variant="outline" className="text-xs">Disabled</Badge>
                  </div>
                  <Progress value={0} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs">Live Chat</span>
                    <Badge variant="outline" className="text-xs">Enabled</Badge>
                  </div>
                  <Progress value={100} />
                </div>
              </div>
              <Button size="sm" className="w-full mt-4">Manage Features</Button>
            </div>
            
            <div className="border border-border rounded-md p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Share2 className="h-4 w-4 text-muted-foreground" />
                Stream Distribution
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Multistreaming</span>
                  <Badge variant="outline" className="text-muted-foreground">Upgrade to Enable</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Stream Recording</span>
                  <Badge variant="outline" className="text-muted-foreground">Enabled</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Auto-Publish</span>
                  <Badge variant="outline" className="text-muted-foreground">Disabled</Badge>
                </div>
              </div>
              <Button size="sm" className="w-full mt-4">Manage Distribution</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const LiveStreaming: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-28 px-4 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-2">
              <Video className="h-7 w-7 text-soundcharts-purple" />
              Live Streaming
            </h1>
            <p className="text-muted-foreground">Connect with your fans through live streams and events</p>
          </div>
        </div>
        
        <div className="animate-slide-up">
          <LiveStreamingSystem />
        </div>
      </main>
    </div>
  );
};

export default LiveStreaming;
